/* eslint-disable no-console */
import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as Yup from 'yup';
import Swal from 'sweetalert2';

import { MdCheck, MdUpload } from 'react-icons/md';
import { addDays, endOfDay, format, parseISO, set } from 'date-fns';
import { IoMdClose } from 'react-icons/io';
import { Form } from '@unform/web';
import { FaRegFilePdf } from 'react-icons/fa6';
import { FormHandles } from '@unform/core';
import { TableList, Container, Filter, Status, Modal } from './styles';

import api from '~/services/api';
import getValidationErros from '~/utils/getValidationsErrors';

import Table, { IColumn } from '~/components/Table';
import FiltersLists from '~/components/FiltersLists';
import ExportCsv from '~/components/ExportCsv';
import { formatPrice } from '~/utils/format';
import Select, { IOption } from '~/components/Select';
import { KrClose, KrEye } from '~/components/KoroIcons';
import Input from '~/components/Input';
import InputDate from '~/components/InputDate';
import Loading from '~/components/Loading';
import Toast from '~/utils/toast';

interface IWithdraw {
  id: number;
  organizer: string;
  request_date: string;
  requesting: string;
  released_value: string;
  amount_requested: string;
  expected_deposit: string;
  deposit_date: string;
  payment_status: string;
  file?: string;
  file_name?: string;
}

interface IOrganizerUser {
  user_id: number;
  user: {
    name: string;
  };
}

interface IWithdrawData {
  id: number;
  value: string;
  status: string;
  created_at: string;
  updated_at: string;
  created_user_id: number;
  archive: {
    archive_url: string;
    name: string;
  };
  organizer: {
    name: string;
    organizersUsers: IOrganizerUser[];
  };
}

interface IWithdrawResponse {
  data: IWithdrawData[];
  from: number;
  to: number;
  total: number;
  pages: number;
}

const Withdrawal: React.FC = () => {
  const formRef = useRef<FormHandles>(null);
  const [pageSelected, setPageSelected] = useState(1);
  const [data, setData] = useState<IWithdraw[]>([]);
  const [tableData, setTableData] = useState({
    from: 0,
    to: 0,
    total: 0,
    totalPage: 0,
  });
  const [organizerSelected, setOrganizerSelected] = useState('');
  const [filesColor, setFilesColor] = useState<File[]>([]);
  const [paymentDate, setPaymentDate] = useState<Date>(new Date());
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(false);
  const [showView, setShowView] = useState(false);
  const [proof, setProof] = useState('');
  const [proofName, setProofName] = useState('');
  const [withdrawSelected, setWithdrawSelected] = useState({} as IWithdraw);
  const [organizers, setOrganizers] = useState<IOption[]>([]);

  interface IOrganizer {
    id: number;
    name: string;
  }

  useEffect(() => {
    api.get<IOrganizer[]>('organizers').then((response) => {
      const dataOrganizers = response.data.map<IOption>((organizer) => ({
        id: organizer.id,
        value: organizer.name,
        selected: false,
      }));
      setOrganizers(dataOrganizers);
    });
  }, []);

  const handleLoadWithdraws = useCallback(
    async (page: number, organizer?: string) => {
      setLoading(true);
      const response = await api
        .get<IWithdrawResponse>('withdrawals/admin', {
          params: {
            page,
            organizer,
          },
        })
        .finally(() => {
          setLoading(false);
        });

      const dataAux = response.data.data.map<IWithdraw>((withdrawal) => {
        let payment_status = 'Pendente';

        if (
          endOfDay(addDays(parseISO(withdrawal.created_at), 2)) <
          endOfDay(new Date())
        ) {
          payment_status = 'Atrasado';
        }

        if (withdrawal.status === 'Pago') {
          payment_status = 'Pago';
        }

        let requesting = '-';

        const organizerUserData = withdrawal.organizer?.organizersUsers.find(
          (organizerUser) =>
            organizerUser.user_id === withdrawal.created_user_id
        );

        if (organizerUserData) {
          requesting = organizerUserData.user.name;
        }

        return {
          id: withdrawal.id,
          organizer: withdrawal.organizer?.name,
          request_date: format(parseISO(withdrawal.created_at), 'dd/MM/yyyy'),
          requesting: requesting || withdrawal.organizer.name,
          released_value: formatPrice(parseFloat(withdrawal.value)),
          amount_requested: formatPrice(parseFloat(withdrawal.value)),
          expected_deposit: format(
            addDays(parseISO(withdrawal.created_at), 2),
            'dd/MM/yyyy'
          ),
          deposit_date:
            payment_status === 'Pago'
              ? format(parseISO(withdrawal.updated_at), 'dd/MM/yyyy')
              : '-',
          payment_status,
          file: withdrawal?.archive?.archive_url,
          file_name: withdrawal?.archive?.name,
        };
      });

      setData(dataAux);
      setTableData({
        from: response.data.from,
        to: response.data.to,
        total: response.data.total,
        totalPage: response.data.pages,
      });
    },
    []
  );

  useEffect(() => {
    handleLoadWithdraws(1);
  }, [handleLoadWithdraws]);

  const handleClickCheck = useCallback((row: IWithdraw) => {
    setWithdrawSelected(row);
    setShow(true);
  }, []);

  const handleClickView = useCallback((row) => {
    setShowView(true);
    setProof(row.file);
    setProofName(row.file_name);
  }, []);

  const handleSubmit = useCallback(
    async (formData) => {
      try {
        setLoading(true);
        console.log(formData);
        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          formPayment: Yup.string().required('O e-mail é obrigatório'),
          paymentDate: Yup.string().required('A senha é obrigatório'),
          receipt: Yup.string().when(`$checkReceipt`, {
            is: (checkReceipt: boolean) => checkReceipt,
            then: Yup.string().required('O comprovante é obrigatório'),
            otherwise: Yup.string(),
          }),
        });

        await schema.validate(formData, {
          abortEarly: false,
          context: {
            checkReceipt: filesColor.length === 0,
          },
        });

        const fileFormData = new FormData();
        fileFormData.append('file', filesColor[0]);
        fileFormData.append('folder', 'receipts');

        const responseFile = await api.post('archives/admin', fileFormData);

        await api.put(`withdrawals/admin/${withdrawSelected.id}`, {
          archive_id: responseFile.data.id,
          payment_type: formData.formPayment,
          payment_date: new Date(formData.paymentDate),
          status: 'Pago',
        });

        const withdrawIndex = data.findIndex(
          (withdraw) => withdraw.id === withdrawSelected.id
        );

        if (withdrawIndex >= 0) {
          data[withdrawIndex].deposit_date = format(new Date(), 'dd/MM/yyyy');
          data[withdrawIndex].payment_status = 'Pago';
          data[withdrawIndex].file = URL.createObjectURL(filesColor[0]);
          data[withdrawIndex].file_name = filesColor[0].name;
        }

        setData(data);
        setShow(false);
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErros(error);
          formRef.current?.setErrors(errors);
        } else {
          Swal.fire('Oops...', 'Credenciais inválidas!');
        }
      } finally {
        setLoading(false);
      }
    },
    [data, filesColor, withdrawSelected.id]
  );

  const handleClose = useCallback(() => {
    setShow(false);
    setShowView(false);
    setFilesColor([]);
  }, []);

  const handleDeleteRow = useCallback(
    (row) => {
      Swal.fire({
        icon: 'warning',
        iconColor: '#ff6900',
        title: `Excluir solicitação de saque de ${row.requesting}?`,
        showCancelButton: true,
        cancelButtonText: 'Não',
        confirmButtonText: 'Sim',
        confirmButtonColor: '#ff6900',
        cancelButtonColor: '#B34A00',
        reverseButtons: true,
      }).then(async (e) => {
        if (e.isConfirmed) {
          await api.delete(`withdrawals/admin/${row.id}`);
          setData(data.filter((item) => item.id !== row.id));
          handleClose();
          Toast.fire({
            icon: 'success',
            iconColor: '#ff6900',
            title: 'solicitação de saque excluída com sucesso!',
          });
        }
      });
    },
    [data, handleClose]
  );

  const columns = useMemo<IColumn[]>(
    () => [
      {
        name: '#',
        selector: 'id',
        sortable: false,
      },

      {
        name: 'Organizador',
        selector: 'organizer',
        sortable: true,
      },
      {
        name: 'Dt Solicitação',
        selector: 'request_date',
        sortable: true,
      },
      {
        name: 'Solicitante',
        selector: 'requesting',
        sortable: true,
      },
      {
        name: 'Vlr Liberado',
        selector: 'released_value',
        sortable: true,
      },
      {
        name: 'Vlr Solicitado',
        selector: 'amount_requested',
        sortable: true,
      },
      {
        name: 'Dt prev. Depósito',
        selector: 'expected_deposit',
        sortable: true,
      },
      {
        name: 'Dt Depósito',
        selector: 'deposit_date',
        sortable: true,
      },

      {
        name: 'Status',
        selector: 'payment_status',
        sortable: true,
        cell: (row) => (
          <Status
            status={row.payment_status}
            className="text-white rounded-pill mb-0"
          >
            {row.payment_status}
          </Status>
        ),
      },

      {
        name: '',
        selector: 'active',
        sortable: false,
        cell: (row) => (
          <>
            <button
              type="button"
              disabled={row.payment_status === 'Pago'}
              onClick={() => handleClickCheck(row)}
              className={`${
                row.payment_status === 'Pago' ? 'opacity-50' : ''
              } btn-icons border-0 me-2`}
            >
              <MdCheck size={16} color="#fff" />
            </button>
            <button
              type="button"
              disabled={row.payment_status !== 'Pago'}
              onClick={() => handleClickView(row)}
              className={`${
                row.payment_status !== 'Pago' ? 'opacity-50' : ''
              } btn-icons border-0 me-2`}
            >
              <KrEye size={16} color="#fff" />
            </button>
            <button
              type="button"
              disabled={row.payment_status === 'Pago'}
              onClick={() => handleDeleteRow(row)}
              className="btn-icons border-0"
            >
              <KrClose size={16} color="#fff" />
            </button>
          </>
        ),
      },
    ],
    [handleClickCheck, handleClickView, handleDeleteRow]
  );

  const handleChangePage = useCallback(
    (page) => {
      if (page <= tableData.totalPage) {
        handleLoadWithdraws(page, organizerSelected);
        setPageSelected(page);
      }
    },
    [handleLoadWithdraws, organizerSelected, tableData.totalPage]
  );

  const handleSubmitFilters = useCallback(
    (formData) => {
      setOrganizerSelected(
        formData.organizer === 'todos' ? undefined : formData.organizer
      );
      handleLoadWithdraws(
        1,
        formData.organizer === 'todos' ? undefined : formData.organizer
      );
      setPageSelected(1);
    },
    [handleLoadWithdraws]
  );

  const handleDeleteColor = useCallback((indexToDelete) => {
    setFilesColor((prevFiles) =>
      prevFiles.filter((file, index) => index !== indexToDelete)
    );
  }, []);

  const handleDropColor = useCallback(
    (event: React.DragEvent<HTMLLabelElement>) => {
      event.preventDefault();
      const { files } = event.dataTransfer;
      if (files) {
        setFilesColor(Array.from(files));
      }
    },
    []
  );

  const handleDragOver = useCallback(
    (event: React.DragEvent<HTMLLabelElement>) => {
      event.preventDefault();
    },
    []
  );

  const handleFileColorChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { files } = event.target;
      if (files) {
        setFilesColor(Array.from(files));
        const inputElement = document.getElementById(
          `photos`
        ) as HTMLInputElement;

        if (inputElement) {
          inputElement.value = '';
        }
      }
    },
    []
  );

  return (
    <Container>
      <div className="container-fluid">
        <div className="row">
          <div className="zoom col-12 px-0">
            <div className="bg-white d-sm-flex justify-content-between align-items-center p-4">
              <h1 className="mb-0">Solicitações de saque</h1>
              <ExportCsv
                data={data}
                columns={columns}
                fileName="Solicitacoes de saque"
              />
            </div>
          </div>
          <Filter className="col-12 px-0 mt-3">
            <FiltersLists
              onSubmitFilters={handleSubmitFilters}
              organizer
              organizersData={organizers}
            />
          </Filter>
          <div className="col-12 px-0">
            <div className="row">
              <TableList className="col-12 mt-3">
                <div className="bg-white pb-2">
                  <div className="bg-white overflow-auto">
                    <div className="">
                      <Table
                        className="mb-0 p-0 table"
                        columns={columns}
                        data={data}
                        pagination
                        totalData={tableData.total}
                        selectedPage={pageSelected}
                        onChangePage={handleChangePage}
                      />
                    </div>
                  </div>
                </div>
              </TableList>
            </div>
          </div>
        </div>
      </div>
      <Modal
        className="modal-filter"
        size="lg"
        centered
        show={show}
        onHide={handleClose}
      >
        <Form onSubmit={handleSubmit}>
          <Modal.Header className="justify-content-between align-items-center border-0 px-3 px-sm-5 pb-0 pt-0">
            <Modal.Title className="mb-0 mt-4">
              <h2 className="title mb-0">Pedido de saque</h2>
            </Modal.Title>
            <button
              type="button"
              onClick={handleClose}
              className="border-0 bg-transparent"
            >
              <KrClose size={20} color="#989898" />
            </button>
          </Modal.Header>
          <Modal.Body className="pt-0 px-3 px-sm-5">
            <div className="row">
              <div className="col-12">
                <hr />
                <h3 className="my-5">Dados do Pagamento</h3>
                <p className="name">
                  Beneficiário: <span>{withdrawSelected.organizer}</span>
                </p>
              </div>
              <div className="col-6">
                <label className="d-block mt-3">
                  <span className="h6 d-block fw-normal">
                    Data do pagamento
                  </span>
                  <InputDate
                    name="paymentDate"
                    className="height"
                    selected={paymentDate}
                    colorSvg="#5D5D5D"
                    onChange={(e) => setPaymentDate(e as Date)}
                  />
                </label>
              </div>
              <div className="col-6">
                <label className="d-block mt-3">
                  <span className="h6 d-block fw-normal">
                    Forma de pagamento
                  </span>
                  <Select
                    name="formPayment"
                    className="input mt-2"
                    options={[
                      {
                        id: 'pix',
                        value: 'PIX',
                        selected: true,
                      },
                      {
                        id: 'ted',
                        value: 'TED',
                        selected: false,
                      },
                      {
                        id: 'doc',
                        value: 'DOC',
                        selected: false,
                      },
                    ]}
                  />
                </label>
              </div>

              <>
                <div className="col-12">
                  <label
                    htmlFor="photos"
                    className="w-100 label-modal text-center mt-3"
                    onDrop={handleDropColor}
                    onDragOver={handleDragOver}
                  >
                    <MdUpload size={48} color="#FF6900" />
                    <span className="d-block">
                      Arraste e solte o arquivo aqui para
                      <br />
                      adicionar um comprovante
                    </span>
                    <span className="d-inline-flex my-2">
                      Selecionar arquivo
                    </span>
                    <span className="d-block">
                      Extensões aceitas: pdf, jpg, png, jpeg.
                    </span>
                  </label>
                  <Input
                    id="photos"
                    type="file"
                    name="photos"
                    className="d-none"
                    onChange={(e) => handleFileColorChange(e)}
                  />
                  {filesColor.map((file, index) => (
                    <div
                      // eslint-disable-next-line react/no-array-index-key
                      key={index}
                      className="d-flex justify-content-between arquives p-3 mt-3"
                    >
                      <div className="d-flex align-items-center">
                        {file.name.toLowerCase().includes('.pdf') ? (
                          <FaRegFilePdf size={30} color="#8c8c8c" />
                        ) : (
                          <img
                            src={URL.createObjectURL(file)}
                            alt={file.name}
                          />
                        )}

                        <p className="ms-3 mb-0">{file.name}</p>
                      </div>
                      <button
                        type="button"
                        className="bg-transparent border-0"
                        onClick={() => handleDeleteColor(index)}
                      >
                        <IoMdClose size={24} color="#8C8C8C" />
                      </button>
                    </div>
                  ))}
                </div>
              </>
            </div>
          </Modal.Body>
          <Modal.Footer className="border-0 pb-4 px-5">
            <button
              type="submit"
              disabled={filesColor.length === 0 || loading}
              className="me-auto btn-filter d-flex align-items-center justify-content-center btn h6 fw-normal py-2 px-4 mt-2 mb-0"
            >
              Confirmar pagamento
            </button>
          </Modal.Footer>
        </Form>
      </Modal>
      <Modal
        className="modal-filter"
        size="lg"
        centered
        show={showView}
        onHide={handleClose}
      >
        <Modal.Header className="justify-content-between align-items-center border-0 px-3 px-sm-5 pb-0 pt-0">
          <Modal.Title className="mb-0 mt-4">
            <h2 className="title mb-0">Comprovante</h2>
          </Modal.Title>
          <button
            type="button"
            onClick={handleClose}
            className="border-0 bg-transparent"
          >
            <KrClose size={20} color="#989898" />
          </button>
        </Modal.Header>
        <Modal.Body className="pt-0 px-3 px-sm-5">
          <div className="row">
            <div className="col-12">
              <hr />
            </div>
            <div className="col-12">
              {proofName.toLowerCase().includes('.pdf') ? (
                <iframe
                  className="w-100"
                  height={500}
                  title="Comprovante"
                  src={proof}
                  frameBorder="0"
                />
              ) : (
                <img className="w-100 mb-5" src={proof} alt={proof} />
              )}
            </div>
          </div>
        </Modal.Body>
      </Modal>
      <Loading active={loading} text="Carregando..." />
    </Container>
  );
};

export default Withdrawal;
